import { useState, useMemo } from 'react';
import {
  Search, Filter, ChevronDown, ChevronRight, Ship, AlertTriangle,
  CheckCircle2, Clock, XCircle, MessageSquare, Palette, Calendar,
  User, AlertCircle, Eye, X, Camera
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { mockFleets } from '../../data/mockData';
import {
  formatDate, getColorCodeLabel, getColorCodeHex, getColorCodeTextClass,
  getJobClassificationLabel, getJobClassificationColor, getCAPAStatusLabel,
  getCAPAStatusColor, getCAPAPriorityColor, ALL_COLOR_CODES, ALL_JOB_CLASSIFICATIONS
} from '../../utils/helpers';
import type { ColorCode, JobClassification, CorrectiveAction } from '../../types';

const STATUS_OPTIONS = ['all', 'open', 'assigned', 'in_progress', 'pending_review', 'closed', 'verified'] as const;

export default function FleetTrackingPage() {
  const { correctiveActions, updateCAPAColorCode, updateCAPACloseout, updateCAPAStatus } = useAppStore();

  // Filters
  const [search, setSearch] = useState('');
  const [selectedFleet, setSelectedFleet] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [colorFilter, setColorFilter] = useState<ColorCode | 'all'>('all');
  const [classFilter, setClassFilter] = useState<JobClassification | 'all'>('all');

  // UI state
  const [expandedVessels, setExpandedVessels] = useState<Set<string>>(new Set());
  const [editingColor, setEditingColor] = useState<string | null>(null);
  const [editingCloseout, setEditingCloseout] = useState<string | null>(null);
  const [closeoutText, setCloseoutText] = useState('');
  const [selectedDetail, setSelectedDetail] = useState<CorrectiveAction | null>(null);
  const [photoModalCapa, setPhotoModalCapa] = useState<CorrectiveAction | null>(null);

  // Filter
  const filtered = useMemo(() => {
    return correctiveActions.filter(c => {
      if (search) {
        const s = search.toLowerCase();
        if (!c.vesselName.toLowerCase().includes(s) && !c.title.toLowerCase().includes(s) && !c.owner.toLowerCase().includes(s)) return false;
      }
      if (selectedFleet !== 'all' && c.fleetId !== selectedFleet) return false;
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (colorFilter !== 'all' && c.colorCode !== colorFilter) return false;
      if (classFilter !== 'all' && c.jobClassification !== classFilter) return false;
      return true;
    });
  }, [correctiveActions, search, selectedFleet, statusFilter, colorFilter, classFilter]);

  // Group by fleet -> vessel
  const grouped = useMemo(() => {
    const fleetMap = new Map<string, { fleet: typeof mockFleets[0]; vessels: Map<string, CorrectiveAction[]> }>();

    for (const capa of filtered) {
      const fleetId = capa.fleetId || 'fl-001';
      if (!fleetMap.has(fleetId)) {
        const fleet = mockFleets.find(f => f.id === fleetId) || mockFleets[0];
        fleetMap.set(fleetId, { fleet, vessels: new Map() });
      }
      const fleetData = fleetMap.get(fleetId)!;
      if (!fleetData.vessels.has(capa.vesselName)) {
        fleetData.vessels.set(capa.vesselName, []);
      }
      fleetData.vessels.get(capa.vesselName)!.push(capa);
    }

    return Array.from(fleetMap.values()).sort((a, b) => a.fleet.name.localeCompare(b.fleet.name));
  }, [filtered]);

  const toggleVessel = (vesselName: string) => {
    setExpandedVessels(prev => {
      const next = new Set(prev);
      if (next.has(vesselName)) next.delete(vesselName);
      else next.add(vesselName);
      return next;
    });
  };

  const expandAll = () => {
    const all = new Set<string>();
    grouped.forEach(f => f.vessels.forEach((_, name) => all.add(name)));
    setExpandedVessels(all);
  };

  const collapseAll = () => setExpandedVessels(new Set());

  const handleColorChange = (capaId: string, color: ColorCode) => {
    updateCAPAColorCode(capaId, color);
    setEditingColor(null);
  };

  const handleSaveCloseout = (capaId: string) => {
    updateCAPACloseout(capaId, closeoutText);
    setEditingCloseout(null);
    setCloseoutText('');
  };

  // KPI summary
  const totalActions = filtered.length;
  const redCount = filtered.filter(c => c.colorCode === 'red').length;
  const yellowCount = filtered.filter(c => c.colorCode === 'yellow').length;
  const greenCount = filtered.filter(c => c.colorCode === 'green').length;
  const orangeCount = filtered.filter(c => c.colorCode === 'orange').length;
  const greyCount = filtered.filter(c => c.colorCode === 'grey_blue').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Fleet Corrective Action Tracker</h1>
          <p className="text-sm text-slate-500 mt-1">Track corrective actions across all fleet vessels with color-coded status</p>
        </div>
        <div className="flex gap-2">
          <button onClick={expandAll} className="px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-xs font-medium hover:bg-slate-50 transition-colors">
            Expand All
          </button>
          <button onClick={collapseAll} className="px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-xs font-medium hover:bg-slate-50 transition-colors">
            Collapse All
          </button>
        </div>
      </div>

      {/* KPI Cards - Color Summary */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {[
          { label: 'Total', value: totalActions, bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' },
          { label: 'RED', value: redCount, bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
          { label: 'YELLOW', value: yellowCount, bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-400' },
          { label: 'GREEN', value: greenCount, bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500' },
          { label: 'ORANGE', value: orangeCount, bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', dot: 'bg-orange-500' },
          { label: 'GREY BLUE', value: greyCount, bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-600', dot: 'bg-slate-400' },
        ].map(kpi => (
          <div key={kpi.label} className={`rounded-2xl p-4 border ${kpi.bg} ${kpi.border}`}>
            <div className="flex items-center gap-2">
              {'dot' in kpi && kpi.dot && <div className={`w-3 h-3 rounded-full ${kpi.dot}`} />}
              <div>
                <p className={`text-2xl font-bold ${kpi.text}`}>{kpi.value}</p>
                <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{kpi.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by vessel, title, or responsible person..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>

          {/* Fleet Filter */}
          <div className="relative min-w-[180px]">
            <Ship className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={selectedFleet}
              onChange={e => setSelectedFleet(e.target.value)}
              className="w-full pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 appearance-none cursor-pointer"
            >
              <option value="all">All Fleets</option>
              {mockFleets.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>

          {/* Color Filter */}
          <div className="relative min-w-[140px]">
            <Palette className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={colorFilter}
              onChange={e => setColorFilter(e.target.value as ColorCode | 'all')}
              className="w-full pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 appearance-none cursor-pointer"
            >
              <option value="all">All Colors</option>
              {ALL_COLOR_CODES.map(c => (
                <option key={c} value={c}>{getColorCodeLabel(c)}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>

          {/* Classification Filter */}
          <div className="relative min-w-[160px]">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={classFilter}
              onChange={e => setClassFilter(e.target.value as JobClassification | 'all')}
              className="w-full pl-10 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 appearance-none cursor-pointer"
            >
              <option value="all">All Jobs</option>
              {ALL_JOB_CLASSIFICATIONS.map(j => (
                <option key={j} value={j}>{getJobClassificationLabel(j)}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex gap-2 flex-wrap mt-3">
          {STATUS_OPTIONS.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                statusFilter === s
                  ? 'bg-[#0A1628] text-white'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {s === 'all' ? 'All Status' : getCAPAStatusLabel(s as any)}
            </button>
          ))}
        </div>
      </div>

      {/* Fleet Tracking Matrix */}
      <div className="space-y-4">
        {grouped.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No corrective actions match your filters</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          grouped.map(({ fleet, vessels }) => (
            <div key={fleet.id} className="bg-white rounded-2xl border border-slate-100">
              {/* Fleet Header */}
              <div className="bg-gradient-to-r from-[#0A1628] to-[#162033] px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Ship className="w-5 h-5 text-sky-400" />
                  <div>
                    <h2 className="text-base font-bold text-white">{fleet.name}</h2>
                    <p className="text-xs text-slate-400">
                      {vessels.size} vessel{vessels.size !== 1 ? 's' : ''} &middot; {Array.from(vessels.values()).reduce((sum, caps) => sum + caps.length, 0)} corrective action{Array.from(vessels.values()).reduce((sum, caps) => sum + caps.length, 0) !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="text-center">
                    <p className="text-white font-bold">{Array.from(vessels.values()).reduce((sum, caps) => sum + caps.filter(c => c.isOverdue).length, 0)}</p>
                    <p className="text-red-400">Overdue</p>
                  </div>
                  <div className="text-center">
                    <p className="text-white font-bold">{Array.from(vessels.values()).reduce((sum, caps) => sum + caps.filter(c => c.status === 'closed' || c.status === 'verified').length, 0)}</p>
                    <p className="text-emerald-400">Closed</p>
                  </div>
                </div>
              </div>

              {/* Fleet Column Headers */}
              <div className="grid grid-cols-[1fr_120px_130px_100px_100px_140px_1fr_80px] gap-2 px-6 py-2.5 bg-slate-50 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <span>Job / Finding</span>
                <span>Responsible</span>
                <span>Job Classification</span>
                <span>Due Date</span>
                <span>Photos</span>
                <span>Color Code</span>
                <span>Close-out / Comments</span>
                <span className="text-right">Status</span>
              </div>

              {/* Vessels */}
              {Array.from(vessels.entries()).map(([vesselName, capas]) => {
                const isExpanded = expandedVessels.has(vesselName);
                const vesselOverdue = capas.filter(c => c.isOverdue).length;
                const vesselClosed = capas.filter(c => c.status === 'closed' || c.status === 'verified').length;

                return (
                  <div key={vesselName} className="border-b border-slate-50 last:border-b-0">
                    {/* Vessel Row */}
                    <button
                      onClick={() => toggleVessel(vesselName)}
                      className="w-full px-6 py-3 flex items-center gap-3 hover:bg-slate-50/50 transition-colors"
                    >
                      <div className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                      <Ship className="w-4 h-4 text-sky-500" />
                      <span className="font-semibold text-slate-800 text-sm">{vesselName}</span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-medium">
                        {capas.length} item{capas.length !== 1 ? 's' : ''}
                      </span>
                      {vesselOverdue > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold">
                          {vesselOverdue} OVERDUE
                        </span>
                      )}
                      {vesselClosed > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-600 text-[10px] font-medium">
                          {vesselClosed} closed
                        </span>
                      )}

                      {/* Mini color distribution */}
                      <div className="ml-auto flex items-center gap-1">
                        {(['red', 'yellow', 'orange', 'green', 'grey_blue'] as ColorCode[]).map(color => {
                          const count = capas.filter(c => c.colorCode === color).length;
                          if (count === 0) return null;
                          return (
                            <div key={color} className="flex items-center gap-1">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getColorCodeHex(color) }} />
                              <span className="text-[10px] text-slate-500 font-medium">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </button>

                    {/* Vessel CAPA Rows */}
                    {isExpanded && (
                      <div className="bg-slate-50/30">
                        {capas.map((capa, idx) => (
                          <div key={capa.id} className={`grid grid-cols-[1fr_120px_130px_100px_100px_140px_1fr_80px] gap-2 px-6 py-3 items-center border-t border-slate-100 hover:bg-white transition-colors ${
                            idx % 2 === 0 ? 'bg-white/50' : ''
                          }`}>
                            {/* Job / Finding */}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${getCAPAPriorityColor(capa.priority)}`}>
                                  {capa.priority}
                                </span>
                                {capa.isOverdue && (
                                  <span className="px-1.5 py-0.5 rounded bg-red-500 text-white text-[9px] font-bold">OVERDUE</span>
                                )}
                              </div>
                              <p className="text-sm font-medium text-slate-800 truncate">{capa.title}</p>
                              <p className="text-[11px] text-slate-400 truncate">{capa.description}</p>
                            </div>

                            {/* Responsible Person */}
                            <div>
                              <p className="text-xs font-medium text-slate-700">{capa.owner}</p>
                            </div>

                            {/* Job Classification */}
                            <div>
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold border ${getJobClassificationColor(capa.jobClassification)}`}>
                                {getJobClassificationLabel(capa.jobClassification)}
                              </span>
                            </div>

                            {/* Due Date */}
                            <div>
                              <p className={`text-xs font-medium ${capa.isOverdue ? 'text-red-600' : capa.daysRemaining < 14 ? 'text-amber-600' : 'text-slate-600'}`}>
                                {formatDate(capa.dueDate)}
                              </p>
                              {capa.daysRemaining > 0 && !capa.isOverdue && (
                                <p className="text-[10px] text-slate-400">{capa.daysRemaining}d left</p>
                              )}
                            </div>

                            {/* Photos */}
                            <div>
                              {capa.photos && capa.photos.length > 0 ? (
                                <div className="flex items-center gap-1.5">
                                  <div className="flex -space-x-1.5">
                                    {capa.photos.slice(0, 3).map((photo, i) => (
                                      <div
                                        key={i}
                                        className="w-7 h-7 rounded-lg border-2 border-white overflow-hidden bg-slate-100 shadow-sm"
                                      >
                                        <img
                                          src={photo}
                                          alt={`Photo ${i + 1}`}
                                          className="w-full h-full object-cover"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPhotoModalCapa(capa);
                                    }}
                                    className="text-[11px] font-semibold text-sky-600 hover:text-sky-700 hover:underline transition-colors"
                                  >
                                    ...
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-slate-300">
                                  <Camera className="w-3.5 h-3.5" />
                                  <span className="text-[10px]">No photos</span>
                                </div>
                              )}
                            </div>

                            {/* Color Code - Editable */}
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingColor(editingColor === capa.id ? null : capa.id);
                                }}
                                className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-200 hover:border-sky-300 transition-colors bg-white w-full"
                              >
                                <div
                                  className="w-5 h-5 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                                  style={{ backgroundColor: getColorCodeHex(capa.colorCode) }}
                                />
                                <span className={`text-xs font-semibold ${getColorCodeTextClass(capa.colorCode)}`}>
                                  {getColorCodeLabel(capa.colorCode)}
                                </span>
                                <ChevronDown className="w-3 h-3 text-slate-400 ml-auto" />
                              </button>

                              {/* Color Picker Dropdown */}
                              {editingColor === capa.id && (
                                <div className="absolute z-50 top-full mt-1 left-0 bg-white border border-slate-200 rounded-xl shadow-2xl p-2 min-w-[180px]">
                                  <p className="text-[10px] font-bold uppercase text-slate-400 px-2 mb-1">Select Color</p>
                                  {ALL_COLOR_CODES.map(color => (
                                    <button
                                      key={color}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleColorChange(capa.id, color);
                                      }}
                                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${
                                        capa.colorCode === color ? 'bg-slate-100' : 'hover:bg-slate-50'
                                      }`}
                                    >
                                      <div className="w-4 h-4 rounded-full shadow-sm" style={{ backgroundColor: getColorCodeHex(color) }} />
                                      <span className="text-xs font-medium text-slate-700">{getColorCodeLabel(color)}</span>
                                      {capa.colorCode === color && <CheckCircle2 className="w-3 h-3 text-sky-500 ml-auto" />}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Close-out / Comments */}
                            <div>
                              {editingCloseout === capa.id ? (
                                <div className="space-y-1.5">
                                  <textarea
                                    value={closeoutText}
                                    onChange={e => setCloseoutText(e.target.value)}
                                    placeholder="Enter close-out comments..."
                                    className="w-full px-3 py-2 bg-white border border-sky-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                                    rows={2}
                                    autoFocus
                                  />
                                  <div className="flex gap-1.5">
                                    <button
                                      onClick={() => handleSaveCloseout(capa.id)}
                                      className="px-2.5 py-1 bg-sky-500 text-white rounded-md text-[10px] font-medium hover:bg-sky-600 transition-colors"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => { setEditingCloseout(null); setCloseoutText(''); }}
                                      className="px-2.5 py-1 bg-slate-200 text-slate-600 rounded-md text-[10px] font-medium hover:bg-slate-300 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingCloseout(capa.id);
                                    setCloseoutText(capa.closeoutComments || '');
                                  }}
                                  className="text-left w-full group"
                                >
                                  {capa.closeoutComments ? (
                                    <div>
                                      <p className="text-xs text-slate-600 line-clamp-2">{capa.closeoutComments}</p>
                                      <p className="text-[10px] text-slate-400 mt-0.5 group-hover:text-sky-500 transition-colors">Click to edit</p>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1 text-slate-400 group-hover:text-sky-500 transition-colors">
                                      <MessageSquare className="w-3 h-3" />
                                      <span className="text-xs">Add close-out comments</span>
                                    </div>
                                  )}
                                </button>
                              )}
                            </div>

                            {/* Status */}
                            <div className="text-right">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getCAPAStatusColor(capa.status)}`}>
                                {getCAPAStatusLabel(capa.status)}
                              </span>
                              <button
                                onClick={() => setSelectedDetail(capa)}
                                className="block mt-1 text-[10px] text-sky-500 hover:text-sky-600 ml-auto"
                              >
                                View Details
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Detail Modal */}
      {selectedDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedDetail(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${getCAPAPriorityColor(selectedDetail.priority)}`}>
                      {selectedDetail.priority}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCAPAStatusColor(selectedDetail.status)}`}>
                      {getCAPAStatusLabel(selectedDetail.status)}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{selectedDetail.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{selectedDetail.vesselName}</p>
                </div>
                <button onClick={() => setSelectedDetail(null)} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <p className="text-sm text-slate-600 mb-4">{selectedDetail.description}</p>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Responsible</p>
                    <p className="text-sm font-semibold text-slate-800">{selectedDetail.owner}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Due Date</p>
                    <p className="text-sm font-semibold text-slate-800">{formatDate(selectedDetail.dueDate)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Job Classification</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold border ${getJobClassificationColor(selectedDetail.jobClassification)}`}>
                      {getJobClassificationLabel(selectedDetail.jobClassification)}
                    </span>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Color Code</p>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full" style={{ backgroundColor: getColorCodeHex(selectedDetail.colorCode) }} />
                      <span className={`text-sm font-semibold ${getColorCodeTextClass(selectedDetail.colorCode)}`}>
                        {getColorCodeLabel(selectedDetail.colorCode)}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedDetail.rootCause && (
                  <div className="bg-amber-50 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase text-amber-600 mb-1">Root Cause</p>
                    <p className="text-sm text-amber-800">{selectedDetail.rootCause}</p>
                  </div>
                )}

                {selectedDetail.actionTaken && (
                  <div className="bg-blue-50 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase text-blue-600 mb-1">Action Taken</p>
                    <p className="text-sm text-blue-800">{selectedDetail.actionTaken}</p>
                  </div>
                )}

                {selectedDetail.closeoutComments && (
                  <div className="bg-emerald-50 rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase text-emerald-600 mb-1">Close-out Comments (Responsible)</p>
                    <p className="text-sm text-emerald-800">{selectedDetail.closeoutComments}</p>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  {selectedDetail.status !== 'verified' && (
                    <button
                      onClick={() => {
                        const workflow = ['open', 'assigned', 'in_progress', 'pending_review', 'closed', 'verified'];
                        const nextIdx = workflow.indexOf(selectedDetail.status) + 1;
                        if (nextIdx < workflow.length) {
                          updateCAPAStatus(selectedDetail.id, workflow[nextIdx] as any);
                          setSelectedDetail({ ...selectedDetail, status: workflow[nextIdx] as any });
                        }
                      }}
                      className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 transition-colors"
                    >
                      Advance Status
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedDetail(null)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Gallery Modal */}
      {photoModalCapa && photoModalCapa.photos && photoModalCapa.photos.length > 0 && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setPhotoModalCapa(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h3 className="text-base font-bold text-slate-900">Photos</h3>
                <p className="text-xs text-slate-500 mt-0.5">{photoModalCapa.title} — {photoModalCapa.vesselName}</p>
              </div>
              <button
                onClick={() => setPhotoModalCapa(null)}
                className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Photo Grid */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
              <div className="grid grid-cols-2 gap-3">
                {photoModalCapa.photos.map((photo, i) => (
                  <div
                    key={i}
                    className="rounded-xl overflow-hidden bg-slate-100 border border-slate-200 shadow-sm"
                  >
                    <img
                      src={photo}
                      alt={`${photoModalCapa.title} - Photo ${i + 1}`}
                      className="w-full h-48 object-cover"
                    />
                    <div className="px-3 py-2 flex items-center justify-between">
                      <span className="text-xs text-slate-500">Photo {i + 1}</span>
                      <span className="text-[10px] text-slate-400">{photoModalCapa.vesselName}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
